import { useState, useEffect, useCallback } from 'react';
import {
  FileText,
  Cpu,
  CheckCircle2,
  Target,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import { analyticsApi } from '../../services/api';

interface MethodStats {
  count: number;
  avg_confidence: number;
}

interface CategoryEntry {
  category: string;
  count: number;
  top_method: string;
  acceptance_rate: number;
}

interface TemplateEntry {
  template_id: string | null;
  template_name: string;
  category: string;
  times_used: number;
  times_accepted: number;
}

interface RecentDecision {
  id: string;
  created_at: string;
  exam_category: string;
  classification_method: string;
  classification_confidence: number;
  template_name: string;
  signal_type: string | null;
  extracted_features: Record<string, unknown> | null;
}

interface ClassificationData {
  period_days: number;
  total_decisions: number;
  avg_confidence: number;
  by_method: Record<string, MethodStats>;
  by_signal: Record<string, number>;
  acceptance_rate: number;
  top_categories: CategoryEntry[];
  templates_used: TemplateEntry[];
  recent_decisions: RecentDecision[];
}

const METHOD_COLORS: Record<string, string> = {
  deterministic: '#22c55e',
  llm: '#3b82f6',
  sklearn: '#a855f7',
  fallback: '#f97316',
};

const METHOD_BADGE: Record<string, string> = {
  deterministic: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  llm: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  sklearn: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
  fallback: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
};

const SIGNAL_BADGE: Record<string, string> = {
  accept_template: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  copy_clipboard: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  reject: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  no_signal: 'bg-gray-100 text-gray-500 dark:bg-surface-800 dark:text-gray-400',
};

export default function ClassificationInsights({ days }: { days: number }) {
  const [data, setData] = useState<ClassificationData | null>(null);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const result = await analyticsApi.classification(days);
      setData(result);
    } catch (err) {
      console.error('Failed to fetch classification data:', err);
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) {
    return <div className="text-center py-12 text-gray-400">Loading...</div>;
  }

  if (!data) {
    return (
      <div className="bg-white dark:bg-surface-900 rounded-lg border border-gray-200 dark:border-surface-800 p-12 text-center">
        <p className="text-gray-400">Failed to load classification data.</p>
      </div>
    );
  }

  const deterministicCount = data.by_method.deterministic?.count || 0;
  const deterministicRate = data.total_decisions > 0
    ? Math.round(deterministicCount / data.total_decisions * 100)
    : 0;

  const methodChartData = Object.entries(data.by_method).map(([method, stats]) => ({
    method,
    count: stats.count,
    avg_confidence: Math.round(stats.avg_confidence * 100),
  }));

  const formatDate = (iso: string) => {
    return new Date(iso).toLocaleString('en-AU', {
      hour12: false,
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const signalLabel = (signal: string | null) => {
    if (!signal) return 'no_signal';
    return signal;
  };

  const signalDisplay = (signal: string | null) => {
    const map: Record<string, string> = {
      accept_template: 'Accepted',
      copy_clipboard: 'Clipboard',
      reject: 'Rejected',
      no_signal: 'No Signal',
    };
    return map[signal || 'no_signal'] || signal || 'No Signal';
  };

  return (
    <div className="space-y-4">
      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          label="Total Decisions"
          value={data.total_decisions}
          icon={<FileText size={20} />}
          color="blue"
        />
        <KpiCard
          label="Deterministic Rate"
          value={`${deterministicRate}%`}
          icon={<Cpu size={20} />}
          color="green"
        />
        <KpiCard
          label="Acceptance Rate"
          value={`${data.acceptance_rate}%`}
          icon={<CheckCircle2 size={20} />}
          color="teal"
        />
        <KpiCard
          label="Avg Confidence"
          value={`${Math.round(data.avg_confidence * 100)}%`}
          icon={<Target size={20} />}
          color="purple"
        />
      </div>

      {/* Methods Chart */}
      <div className="bg-white dark:bg-surface-900 rounded-lg border border-gray-200 dark:border-surface-800 p-4">
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-4">Classification Methods</h3>
        {methodChartData.length > 0 ? (
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={methodChartData} layout="horizontal">
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="method" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip
                formatter={(value, name) => {
                  if (name === 'count') return [value as number, 'Count'];
                  if (name === 'avg_confidence') return [`${value}%`, 'Avg Confidence'];
                  return [value as number, String(name)];
                }}
              />
              <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                {methodChartData.map((entry) => (
                  <Cell key={entry.method} fill={METHOD_COLORS[entry.method] || '#6b7280'} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-[220px] flex items-center justify-center text-gray-400 text-sm">No data yet</div>
        )}
      </div>

      {/* Top Categories */}
      <div className="bg-white dark:bg-surface-900 rounded-lg border border-gray-200 dark:border-surface-800">
        <div className="px-4 py-3 border-b border-gray-200 dark:border-surface-800">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200">Top Categories</h3>
        </div>
        {data.top_categories.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide border-b border-gray-100 dark:border-surface-800">
                  <th className="px-4 py-2 text-left font-medium">Category</th>
                  <th className="px-4 py-2 text-right font-medium">Count</th>
                  <th className="px-4 py-2 text-left font-medium">Primary Method</th>
                  <th className="px-4 py-2 text-right font-medium">Acceptance Rate</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50 dark:divide-surface-800">
                {data.top_categories.map((cat) => (
                  <tr key={cat.category} className="hover:bg-gray-50 dark:hover:bg-surface-800/50">
                    <td className="px-4 py-2.5 text-gray-700 dark:text-gray-300 font-medium">{cat.category}</td>
                    <td className="px-4 py-2.5 text-right text-gray-600 dark:text-gray-400">{cat.count}</td>
                    <td className="px-4 py-2.5">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${METHOD_BADGE[cat.top_method] || 'bg-gray-100 text-gray-500'}`}>
                        {cat.top_method}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-right text-gray-600 dark:text-gray-400">{cat.acceptance_rate}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="px-4 py-8 text-center text-gray-400 text-sm">No category data yet.</div>
        )}
      </div>

      {/* Templates Used */}
      <div className="bg-white dark:bg-surface-900 rounded-lg border border-gray-200 dark:border-surface-800">
        <div className="px-4 py-3 border-b border-gray-200 dark:border-surface-800">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200">Templates Used</h3>
        </div>
        {data.templates_used.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide border-b border-gray-100 dark:border-surface-800">
                  <th className="px-4 py-2 text-left font-medium">Template Name</th>
                  <th className="px-4 py-2 text-left font-medium">Category</th>
                  <th className="px-4 py-2 text-right font-medium">Times Used</th>
                  <th className="px-4 py-2 text-right font-medium">Times Accepted</th>
                  <th className="px-4 py-2 text-right font-medium">Accept %</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50 dark:divide-surface-800">
                {data.templates_used.map((tmpl) => (
                  <tr key={tmpl.template_id || tmpl.template_name} className="hover:bg-gray-50 dark:hover:bg-surface-800/50">
                    <td className="px-4 py-2.5 text-gray-700 dark:text-gray-300 font-medium">{tmpl.template_name}</td>
                    <td className="px-4 py-2.5 text-gray-500 dark:text-gray-400">{tmpl.category || '—'}</td>
                    <td className="px-4 py-2.5 text-right text-gray-600 dark:text-gray-400">{tmpl.times_used}</td>
                    <td className="px-4 py-2.5 text-right text-gray-600 dark:text-gray-400">{tmpl.times_accepted}</td>
                    <td className="px-4 py-2.5 text-right text-gray-600 dark:text-gray-400">
                      {tmpl.times_used > 0 ? Math.round(tmpl.times_accepted / tmpl.times_used * 100) : 0}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="px-4 py-8 text-center text-gray-400 text-sm">No template data yet.</div>
        )}
      </div>

      {/* Recent Decisions */}
      <div className="bg-white dark:bg-surface-900 rounded-lg border border-gray-200 dark:border-surface-800">
        <div className="px-4 py-3 border-b border-gray-200 dark:border-surface-800">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200">Recent Decisions</h3>
        </div>
        <div className="divide-y divide-gray-100 dark:divide-surface-800 max-h-[600px] overflow-y-auto">
          {data.recent_decisions.length === 0 && (
            <div className="px-4 py-8 text-center text-gray-400 text-sm">No decisions yet. Classification data will appear when reports are generated.</div>
          )}
          {data.recent_decisions.map((d) => (
            <div key={d.id} className="hover:bg-gray-50 dark:hover:bg-surface-800/50">
              <div
                className="px-4 py-2.5 flex items-center gap-3 cursor-pointer"
                onClick={() => setExpandedId(expandedId === d.id ? null : d.id)}
              >
                {expandedId === d.id ? (
                  <ChevronUp size={14} className="text-gray-400 flex-shrink-0" />
                ) : (
                  <ChevronDown size={14} className="text-gray-400 flex-shrink-0" />
                )}

                <span className="text-xs text-gray-500 dark:text-gray-400 w-28 flex-shrink-0">
                  {formatDate(d.created_at)}
                </span>

                <span className="text-sm text-gray-700 dark:text-gray-300 truncate flex-1">
                  {d.exam_category}
                </span>

                <span className={`text-xs px-2 py-0.5 rounded-full font-medium flex-shrink-0 ${METHOD_BADGE[d.classification_method] || 'bg-gray-100 text-gray-500'}`}>
                  {d.classification_method}
                </span>

                <span className="text-xs text-gray-400 flex-shrink-0 w-12 text-right">
                  {Math.round(d.classification_confidence * 100)}%
                </span>

                <span className="text-xs text-gray-500 dark:text-gray-400 truncate flex-shrink-0 max-w-[140px]">
                  {d.template_name}
                </span>

                <span className={`text-xs px-2 py-0.5 rounded-full font-medium flex-shrink-0 ${SIGNAL_BADGE[signalLabel(d.signal_type)] || 'bg-gray-100 text-gray-500'}`}>
                  {signalDisplay(d.signal_type)}
                </span>
              </div>

              {expandedId === d.id && d.extracted_features && (
                <div className="px-4 pb-3 ml-7">
                  <div className="bg-gray-50 dark:bg-surface-800 rounded-lg p-3 text-xs space-y-1">
                    <p className="font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5">Extracted Features</p>
                    <div className="flex flex-wrap gap-3 text-gray-600 dark:text-gray-300">
                      {Object.entries(d.extracted_features).map(([key, val]) => {
                        if (val === null || val === undefined || val === '') return null;
                        const display = Array.isArray(val) ? val.join(', ') : String(val);
                        if (!display) return null;
                        return (
                          <span key={key}>
                            <span className="text-gray-400">{key}:</span> {display}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function KpiCard({
  label,
  value,
  icon,
  color,
}: {
  label: string;
  value: string | number;
  icon: React.ReactNode;
  color: string;
}) {
  const colors: Record<string, string> = {
    blue: 'text-blue-600 bg-blue-50 dark:bg-blue-900/20 dark:text-blue-400',
    green: 'text-green-600 bg-green-50 dark:bg-green-900/20 dark:text-green-400',
    teal: 'text-teal-600 bg-teal-50 dark:bg-teal-900/20 dark:text-teal-400',
    purple: 'text-purple-600 bg-purple-50 dark:bg-purple-900/20 dark:text-purple-400',
    orange: 'text-orange-600 bg-orange-50 dark:bg-orange-900/20 dark:text-orange-400',
  };

  return (
    <div className="bg-white dark:bg-surface-900 rounded-lg border border-gray-200 dark:border-surface-800 p-4">
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-lg ${colors[color]}`}>{icon}</div>
        <div>
          <p className="text-xs text-gray-500 dark:text-gray-400">{label}</p>
          <p className="text-xl font-bold text-gray-900 dark:text-white">{value}</p>
        </div>
      </div>
    </div>
  );
}
